import React, { useState, useEffect } from "react";
import { Button, Form } from "react-bootstrap";
import { CourseStatus, CourseState } from "../../edt/scheduler";
import { Input, Space } from "antd";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import { getAllClassroom } from "../../../api/pedagogicElement";
import { toast } from "sonner";
import { updateEDTCours } from "../../../api/edt";
import { usePermissions } from "../../../core/common/context/PermissionsUserContext";
import { useSelector } from "react-redux";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";

const ReportCourseBubble = ({
  course,
  open,
  dayIndex,
  sessionIndex,
  onSave,
  onCancel,
}: {
  course: any;
  open: any;
  dayIndex: number;
  sessionIndex: number;
  onSave: () => void;
  onCancel: () => void;
}) => {
  const { selectedYear } = useAcademicYear();

  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [courseCopy, setCourseCopy] = useState<any>(course);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [allClassrooms, setAllClassrooms] = useState<any[]>();

  // permissions de l'utilisateur connecté
  const { permissions } = usePermissions();
  const isSuperAdmin = useSelector(
    (state: any) => state.auth.user.isSuperAdmin,
  );

  // condition pour vérifier si droit d'édition ou pas
  const canEdit = (): boolean => {
    return (
      [buildPermission(Resource.TEACHER_REPORT, Action.UPDATE)].some(
        (requiredPerm: any) => permissions.includes(requiredPerm),
      ) || isSuperAdmin
    );
  };

  // Fonction pour récupérer toutes les salles de classes de l'année sélectionnée
  const fetchAllClassroom = async () => {
    try {
      if (!selectedYear?.id) return;
      const response = await getAllClassroom(
        selectedYear?.id,
        buildPermissionContext(Resource.TEACHER_REPORT, Action.READ),
      );
      setAllClassrooms(response.data);
    } catch (error: any) {
      // console.log("erreur de chargement des salles de classes : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      if (!courseCopy?.id) return;
      if (courseCopy?.courseDelay < 0) return;

      const body = {
        state_course: courseCopy.state_course,
        type_course: courseCopy.type_course,
        time:
          courseCopy.state_course === "Confirmé"
            ? courseCopy.courseDelay
            : undefined,
        reason_cancel:
          courseCopy.state_course === "Annulé"
            ? courseCopy.reason_cancel || ""
            : "",
        reason_delay:
          courseCopy.state_course === "Confirmé" &&
          courseCopy.courseDelay > 0 &&
          courseCopy?.type_delay === "Retard"
            ? courseCopy.reason_delay || ""
            : "",
        type_delay: courseCopy?.type_delay,
        date_course: courseCopy.date_course,
        id_sessionStart: courseCopy.id_sessionStart,
        id_sessionEnd: courseCopy.id_sessionEnd,
      };

      const responses = await Promise.all(
        courseCopy?.CourseClasses?.map(async (cc: any) => {
          const resp = await updateEDTCours(
            cc.id,
            body,
            buildPermissionContext(Resource.TEACHER_REPORT, Action.UPDATE),
          );
          return resp;
        }),
      );
      // console.log("responses: ", responses);
      onSave();
      onCancel();
      toast.success("Cours modifié avec succès");
    } catch (error: any) {
      // console.log("erreur de modification du cours : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedYear?.id) return;
    fetchAllClassroom();
  }, []);

  useEffect(() => {
    if (!open[`${dayIndex}-${sessionIndex}`]) setCourseCopy(null);
    setCourseCopy(course);
  }, [open]);

  return (
    <div style={{ padding: "15px", width: "350px" }}>
      <p>
        <strong>Salle: </strong>
        {allClassrooms?.find((c) => c.id === courseCopy.id_classroom)?.label ||
          "Non défini"}
      </p>
      {/* <hr /> */}
      <Form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (!e.currentTarget.checkValidity()) {
            e.currentTarget.reportValidity();
            return;
          }
          handleSave();
        }}
      >
        <Form.Group>
          <div className="d-flex justify-content-between">
            <Form.Check
              type="switch"
              id={`${course?.id}-cancel-switch`}
              label="Annuler"
              checked={courseCopy.state_course === CourseStatus.CANCELLED}
              disabled={!canEdit()}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setCourseCopy({
                  ...courseCopy,
                  state_course: e.target.checked
                    ? CourseStatus.CANCELLED
                    : CourseStatus.SCHEDULED,
                  type_delay: e.target.checked
                    ? courseCopy?.type_delay
                    : undefined,
                });
              }}
            />
            <Form.Check
              type="switch"
              id={`${course?.id}-makeup-switch`}
              label="Rattrapage"
              checked={courseCopy.type_course === CourseState.RATTRAPAGE}
              disabled={!canEdit()}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setCourseCopy({
                  ...courseCopy,
                  type_course: e.target.checked
                    ? CourseState.RATTRAPAGE
                    : CourseState.NORMAL,
                });
              }}
            />
          </div>
          {courseCopy.state_course !== CourseStatus.CANCELLED && (
            <Form.Group>
              <Form.Label>Retard (en minutes)</Form.Label>
              <Input
                type="number"
                value={courseCopy?.courseDelay}
                disabled={!canEdit() || courseCopy?.type_delay === "Abscence"}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (isNaN(val)) {
                    setErrorMsg("Veuillez entrer un nombre valide.");
                    return;
                  }
                  if (val < 0) {
                    setErrorMsg("Le retard ne peux pas être négatif.");
                    return;
                  } else setErrorMsg(null);
                  const nextType = val > 0 ? "Retard" : undefined;
                  setCourseCopy({
                    ...courseCopy,
                    courseDelay: val,
                    type_delay: nextType,
                  });
                }}
                placeholder="Temps de retard en minutes"
                style={{ width: "100%" }}
              />
              {/* Show reason textarea if delay > 0 */}
              {courseCopy.courseDelay > 0 && (
                <div style={{ marginTop: "0.5rem" }}>
                  <Form.Label>
                    Cause{" "}
                    {courseCopy?.type_delay === "Abscence"
                      ? "de l'absence"
                      : "du retard"}
                    <Form.Text className="text-danger"> *</Form.Text>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={courseCopy?.reason_delay || ""}
                    disabled={!canEdit()}
                    onChange={(e) => {
                      setCourseCopy({
                        ...courseCopy,
                        reason_delay: e.target.value,
                      });
                    }}
                    placeholder="Décrivez la cause du retard..."
                    required={true}
                  />
                </div>
              )}
            </Form.Group>
          )}
          {errorMsg && <small className="text-danger">{errorMsg}</small>}
          {courseCopy.state_course === CourseStatus.CANCELLED && (
            <div style={{ marginTop: "0.5rem" }}>
              <Form.Group>
                <div className="d-flex justify-content-between">
                  <Form.Label>
                    Raison de l'annulation
                    <Form.Text className="text-danger"> *</Form.Text>
                  </Form.Label>
                  <div>
                    <Input
                      aria-label="Abscent"
                      type="checkbox"
                      checked={courseCopy?.type_delay === "Abscence"}
                      onClick={() => {
                        const nextType =
                          courseCopy?.type_delay === "Abscence"
                            ? undefined
                            : "Abscence";

                        setCourseCopy((prev: any) => ({
                          ...prev,
                          type_delay: nextType,
                          state_course: CourseStatus.CANCELLED,
                        }));
                      }}
                      style={{ width: "15px", margin: "2px" }}
                    />
                    <Form.Label>Abscence ?</Form.Label>
                  </div>
                </div>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={courseCopy?.reason_cancel}
                  disabled={!canEdit()}
                  onChange={(e) => {
                    setCourseCopy({
                      ...courseCopy,
                      reason_cancel: e.target.value,
                    });
                  }}
                  placeholder="Entrez la raison ici..."
                  required={true}
                />
              </Form.Group>
            </div>
          )}
        </Form.Group>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "20px",
          }}
        >
          <Space>
            <Button variant="danger" onClick={onCancel}>
              Annuler
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading || !canEdit()}
            >
              {isLoading ? "Enregistremenrt..." : "Enregistrer"}
            </Button>
          </Space>
        </div>
      </Form>
    </div>
  );
};

export default ReportCourseBubble;
